"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import type { CustomerDetails } from "@/types/customer";
import type { MapCategory } from "@/types/map";

const mapCategories = ["GOLF", "NORMAL"] as const;

interface CreateMapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers?: CustomerDetails[];
  preSelectedCustomer?: CustomerDetails;
  onCreateMap: (data: { 
    name: string; 
    customer: { 
      id: string; 
      name: string; 
    }; 
    category: MapCategory;
  }) => Promise<void>;
  isLoading?: boolean;
}

export function CreateMapDialog({ 
  open, 
  onOpenChange, 
  customers = [],
  preSelectedCustomer,
  onCreateMap,
  isLoading = false
}: CreateMapDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    category: "NORMAL" as MapCategory,
    customer: preSelectedCustomer ? { 
      id: preSelectedCustomer.id, 
      name: preSelectedCustomer.name 
    } : { 
      id: "", 
      name: "" 
    }
  });

  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        name: "",
        category: "NORMAL",
        customer: preSelectedCustomer ? { 
          id: preSelectedCustomer.id, 
          name: preSelectedCustomer.name 
        } : prev.customer
      }));
    }
  }, [open, preSelectedCustomer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.customer.id) return;
    
    try {
      await onCreateMap({
        name: formData.name,
        category: formData.category,
        customer: formData.customer
      });
    } catch (error) {
      console.error("Error creating map:", error);
    }
  };

  const customerItems = customers.map(customer => ({
    label: customer.name,
    value: customer.id
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Map</DialogTitle>
          <DialogDescription>
            Fill in the details for your new map.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Map Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter map name"
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer">Customer</Label>
            {isLoading ? (
              <div className="w-full h-9 rounded-md border bg-muted flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-r-transparent" />
              </div>
            ) : (
              <Combobox
                items={customerItems}
                value={formData.customer.id}
                onChange={(value) => {
                  const customer = customers?.find(c => c.id === value);
                  if (customer) {
                    setFormData(prev => ({
                      ...prev,
                      customer: {
                        id: customer.id,
                        name: customer.name
                      }
                    }));
                  }
                }}
                onSelect={(value) => {
                  const customer = customers?.find(c => c.id === value);
                  if (customer) {
                    setFormData(prev => ({
                      ...prev,
                      customer: {
                        id: customer.id,
                        name: customer.name
                      }
                    }));
                  }
                }}
                placeholder="Search for a customer..."
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Map Type</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as MapCategory }))}
              required
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select map type" />
              </SelectTrigger>
              <SelectContent>
                {mapCategories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category.charAt(0) + category.slice(1).toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !formData.name || !formData.customer.id}
            >
              Create Map
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 